import type { LucideIcon } from 'lucide-react';
import { GripVertical, Maximize2, X } from 'lucide-react';
import type {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from '@dnd-kit/core';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils/cn';
import { SIZE_DEFS, SIZE_ORDER, type WidgetSize } from '../config';

interface WidgetCardProps {
  title: string;
  icon: LucideIcon;
  editing?: boolean;
  onRemove?: () => void;
  description?: string;
  children?: React.ReactNode;
  className?: string;
  /** @dnd-kit attributes / listeners applied to the drag handle so only
   *  the handle initiates a drag. */
  dragAttributes?: DraggableAttributes;
  dragListeners?: DraggableSyntheticListeners;
  /** Current size + setter for the size cycler (only used in customize
   *  mode). */
  size?: WidgetSize;
  onSizeChange?: (size: WidgetSize) => void;
  /**
   * Compact mode hides the chrome header in display mode — used for KPI
   * tiles whose body already shows the title + icon. In editing mode the
   * header still appears so users can drag, resize, and remove.
   */
  compact?: boolean;
}

export function WidgetCard({
  title,
  icon: Icon,
  editing,
  onRemove,
  description,
  children,
  className,
  dragAttributes,
  dragListeners,
  size,
  onSizeChange,
  compact = false,
}: WidgetCardProps) {
  // Compact widgets (KPI tiles) skip the chrome header in display mode —
  // their body already carries the icon, title, and value, so an extra
  // header bar would duplicate the title. The header still shows up when
  // editing so users can drag, resize, and remove the tile.
  const showHeader = editing || !compact;
  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col overflow-hidden rounded-xl border bg-card shadow-sm transition-shadow',
        editing
          ? 'border-primary/40 ring-1 ring-primary/20'
          : 'border-border',
        className,
      )}
    >
      {showHeader && (
      <div className="flex items-start gap-2 border-b border-border bg-muted/30 px-3 py-2">
        {/* Drag handle — ONLY this element listens for drag-start events. */}
        {editing && (
          <button
            type="button"
            {...dragAttributes}
            {...(dragListeners as React.HTMLAttributes<HTMLButtonElement>)}
            className="mt-0.5 flex h-5 w-5 cursor-grab items-center justify-center rounded text-muted-foreground hover:bg-accent/60 active:cursor-grabbing"
            aria-label="Drag handle"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        )}
        <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-semibold uppercase tracking-wider text-foreground">
            {title}
          </div>
          {description && (
            <div className="truncate text-[10px] text-muted-foreground">
              {description}
            </div>
          )}
        </div>
        {editing && size && onSizeChange && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                aria-label="Resize"
                title={`Size: ${SIZE_DEFS[size].label}`}
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-32">
              <DropdownMenuLabel>Size</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {SIZE_ORDER.map((s) => (
                <DropdownMenuItem
                  key={s}
                  onClick={() => onSizeChange(s)}
                  className={cn(
                    s === size && 'bg-primary/10 font-semibold text-primary',
                  )}
                >
                  {SIZE_DEFS[s].label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {editing && onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-destructive hover:text-destructive"
            onClick={onRemove}
            aria-label="Remove widget"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      )}
      <div className={cn('min-h-0 flex-1 overflow-auto', compact ? 'p-2.5' : 'p-3')}>
        {children}
      </div>
    </div>
  );
}
